/**
 * Caches an in-flight/resolved promise from an async factory so concurrent and
 * subsequent callers share a single execution ("single flight"). If the factory
 * rejects, the cache is cleared so a later call can retry.
 *
 * Used to de-duplicate identical requests on cold start (e.g. `getCountries()`
 * called by both the root layout load and the root page redirect), mirroring the
 * `authInitPromise` pattern in `api.ts`.
 */
export function createSingleFlightCache<T>(factory: () => Promise<T>): {
  get: () => Promise<T>;
  reset: () => void;
} {
  let cached: Promise<T> | null = null;

  const get = (): Promise<T> => {
    if (!cached) {
      cached = factory().catch((error) => {
        // Reset on error so a subsequent call can retry.
        cached = null;
        throw error;
      });
    }
    return cached;
  };

  const reset = () => {
    cached = null;
  };

  return { get, reset };
}
